// Common column definitions
const STANDARD_COLUMNS = {
  month_1_lv: { name: '1st Month LV' },
  month_1_sv: { name: '1st Month SV' },
  month_2_lv: { name: '2nd Month LV' },
  month_2_sv: { name: '2nd Month SV' },
  month_3_lv: { name: '3rd Month LV' },
  month_3_sv: { name: '3rd Month SV' }
};

const MEASURE_COLUMN = {
  investment_grade_measure_code: { name: 'Measure (Duration/WAT/10-year eq.)' }
};

const COLUMNS_WITH_MEASURE = {
  ...MEASURE_COLUMN,
  ...STANDARD_COLUMNS
};

// Helper function to create a child item
const createChild = (name, value, includeMeasure = false) => ({
  name,
  value,
  columns: includeMeasure ? COLUMNS_WITH_MEASURE : STANDARD_COLUMNS
});

// Helper function to create a child with measure variant
const createChildWithMeasure = (name, value, measureValue) => [
  createChild(name, value, false),
  createChild(`${name} - Measure`, measureValue, true)
];

// Helper function to create a category with children
const createCategory = (name, children) => ({
  name,
  children
});

// Helper function to create a simple category (no children, direct value)
const createSimpleCategory = (name, value) => ({
  name,
  value,
  columns: STANDARD_COLUMNS
});

export const HEDGE_FUND_AGGREGATED_ASSETS_EXPOSURE_COLUMNS = {
  questionLevels: [
    // Listed equity
    createCategory('Listed equity', [
      createChild('Issued by financial institutions', 'LST_EQT_FIN'),
      createChild('Other listed equity', 'LST_EQT_OTH_LST_EQT')
    ]),

    // Unlisted equity
    createCategory('Unlisted equity', [
      createChild('Issued by financial institutions', 'UNLST_EQT_FIN'),
      createChild('Other unlisted equity', 'UNLST_EQT_OTH_UNLST')
    ]),

    // Listed equity derivatives
    createCategory('Listed equity derivatives', [
      createChild('Related to financial institutions', 'LST_EQT_DER_FIN'),
      createChild('Other listed equity derivatives', 'LST_EQT_DER_OTH_DER')
    ]),

    // Derivative exposures to unlisted equities
    createCategory('Derivative exposures to unlisted equities', [
      createChild('Related to financial institutions', 'DER_EXP_ULST_SEC_FIN'),
      createChild('Other derivative exposures to unlisted equities', 'DER_EXP_ULST_SEC_OTH')
    ]),

    // Corporate bonds issued by financial institutions
    createCategory(
      'Corporate bonds issued by financial institutions (other than convertible bonds)',
      [
        ...createChildWithMeasure(
          'Investment grade',
          'CRP_BND_FIN_INV_GD',
          'CRP_BND_FIN_INV_GD_MSR'
        ),
        ...createChildWithMeasure(
          'Non-investment grade',
          'CRP_BND_FIN_NINV_GD',
          'CRP_BND_FIN_NINV_GD_MSR'
        )
      ]
    ),

    // Corporate bonds not issued by financial institutions
    createCategory(
      'Corporate bonds not issued by financial institutions (other than convertible bonds)',
      [
        ...createChildWithMeasure(
          'Investment grade',
          'CRP_BND_NFIN_INV_GD',
          'CRP_BND_NFIN_INV_GD_MSR'
        ),
        ...createChildWithMeasure(
          'Non-investment grade',
          'CRP_BND_NFIN_NINV_GD',
          'CRP_BND_NFIN_NINV_GD_MSR'
        )
      ]
    ),

    // Convertible bonds issued by financial institutions
    createCategory('Convertible bonds issued by financial institutions', [
      ...createChildWithMeasure('Investment grade', 'CNV_BND_FIN_INV_GD', 'CNV_BND_FIN_INV_GD_MSR'),
      ...createChildWithMeasure(
        'Non-investment grade',
        'CNV_BND_FIN_NINV_GD',
        'CNV_BND_FIN_NINV_GD_MSR'
      )
    ]),

    // Convertible bonds not issued by financial institutions
    createCategory('Convertible bonds not issued by financial institutions', [
      ...createChildWithMeasure(
        'Investment grade',
        'CNV_BND_NFIN_INV_GD',
        'CNV_BND_NFIN_INV_GD_MSR'
      ),
      ...createChildWithMeasure(
        'Non-investment grade',
        'CNV_BND_NFIN_NINV_GD',
        'CNV_BND_NFIN_NINV_GD_MSR'
      )
    ]),

    // Sovereign bonds and municipal bonds
    createCategory('Sovereign bonds and municipal bonds', [
      ...createChildWithMeasure(
        'U.S. treasury securities',
        'SVRN_BMB_US_TRS',
        'SVRN_BMB_US_TRS_MSR'
      ),
      ...createChildWithMeasure('Agency securities', 'SVRN_BMB_AS', 'SVRN_BMB_AS_MSR'),
      ...createChildWithMeasure('GSE bonds', 'SVRN_BMB_GSE_BND', 'SVRN_BMB_GSE_BND_MSR'),
      ...createChildWithMeasure(
        'Sovereign bonds issued by G10 countries other than the U.S.',
        'SVRN_BMB_G10_NO_US',
        'SVRN_BMB_G10_NO_US_MSR'
      ),
      ...createChildWithMeasure(
        'Other sovereign bonds (including supranational bonds)',
        'SVRN_BMB_OTH_SUP_BND',
        'SVRN_BMB_OTH_SUP_BND_MSR'
      ),
      ...createChildWithMeasure(
        'U.S. state and local bonds',
        'SVRN_BMB_US_ST_LCL',
        'SVRN_BMB_US_ST_LCL_MSR'
      )
    ]),

    // Loans
    createCategory('Loans', [
      ...createChildWithMeasure('Leveraged loans', 'LOAN_LVRG_LN', 'LOAN_LVRG_LN_MSR')
    ]),

    // Repos
    createCategory('Repos', [...createChildWithMeasure('Repos', 'REPOS', 'REPOS_MSR')]),

    // ABS/structured products
    createCategory('ABS/structured products', [
      ...createChildWithMeasure('MBS', 'ABS_STRUCT_PRD_MBS', 'ABS_STRUCT_PRD_MBS_MSR'),
      ...createChildWithMeasure('ABCP', 'ABS_STRUCT_PRD_ABCP', 'ABS_STRUCT_PRD_ABCP_MSR'),
      ...createChildWithMeasure('CDO/CLO', 'ABS_STRUCT_PRD_CDO', 'ABS_STRUCT_PRD_CDO_MSR'),
      ...createChildWithMeasure('Other ABS', 'ABS_STRUCT_PRD_OABS', 'ABS_STRUCT_PRD_OABS_MSR'),
      createChild('Other structured products', 'ABS_STRUCT_PRD_OSPRD')
    ]),

    // Credit derivatives
    createCategory('Credit derivatives', [
      createChild('Single name CDS', 'CRD_DER_SNM_CDS'),
      createChild('Index CDS', 'CRD_DER_IDX_CDS'),
      createChild('Exotic CDS', 'CRD_DER_EXO_CDS')
    ]),

    // Simple categories (no children)
    createSimpleCategory('Foreign exchange derivatives (investment)', 'FX_DER_INV'),
    createSimpleCategory('Foreign exchange derivatives (hedging)', 'FX_DER_HDGE'),
    createSimpleCategory('Non-U.S. currency holdings', 'NON_US_CURR_HLDNG'),
    createSimpleCategory('Interest rate derivatives', 'INT_DER'),

    // Commodities (derivatives)
    createCategory('Commodities (derivatives)', [
      createChild('Crude oil', 'COMM_DER_CRUD_OIL'),
      createChild('Natural gas', 'COMM_DER_NG'),
      createChild('Gold', 'COMM_DER_GOLD'),
      createChild('Power', 'COMM_DER_POW'),
      createChild('Other commodities', 'COMM_DER_OTH_COMM')
    ]),

    // More simple categories
    createSimpleCategory('Other derivatives', 'OTH_DER'),
    createSimpleCategory('Physical real estate', 'PHYS_REAL_EST'),
    createSimpleCategory('Investments in internal private funds', 'INV_INT_PVT_FNDS'),
    createSimpleCategory('Investments in external private funds', 'INV_EXT_PVT_FNDS'),
    createSimpleCategory('Investments in registered investment companies', 'INV_REG_INV_COM'),

    // Cash and cash equivalents
    createCategory('Cash and cash equivalents', [
      ...createChildWithMeasure('Certificates of deposit', 'CASH_EQV_CD', 'CASH_EQV_CD_MSR'),
      createChild('Other deposits', 'CASH_EQV_OTHR_DPST'),
      createChild('Money market funds', 'CASH_EQV_MMF'),
      createChild('Other cash and cash equivalents', 'CASH_EQV_OTHR')
    ]),

    // Final simple categories
    createSimpleCategory(
      'Investments in funds for cash management purposes (other than money market funds)',
      'INV_FND_CSHMG_OTH_MM'
    )
    // createSimpleCategory('Investments in other sub-asset classes', 'INV_OTHER_SUBASSETS')
  ]
};
